import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Database, RefreshCw, Upload } from "lucide-react";

export const StripeSync = () => {
  const [loading, setLoading] = useState({
    setup: false,
    backfill: false,
  });
  const [status, setStatus] = useState({
    setup: false,
    backfill: false,
  });
  const { toast } = useToast();

  const runSetup = async () => {
    setLoading((prev) => ({ ...prev, setup: true }));

    try {
      console.log("🚀 Running Stripe sync setup...");

      const { data, error } = await supabase.functions.invoke(
        "stripe-sync-setup",
        {
          body: {},
        },
      );

      if (error) throw error;

      setStatus((prev) => ({ ...prev, setup: true }));
      toast({
        title: "Setup Complete!",
        description: "Stripe sync tables created successfully.",
      });
    } catch (error: any) {
      console.error("Setup error:", error);
      const errorData = error?.data || error;
      toast({
        variant: "destructive",
        title: "Setup Failed",
        description:
          errorData?.hint ||
          errorData?.error ||
          error.message ||
          "Failed to create Stripe sync tables.",
      });
    } finally {
      setLoading((prev) => ({ ...prev, setup: false }));
    }
  };

  const runBackfill = async () => {
    setLoading((prev) => ({ ...prev, backfill: true }));

    try {
      console.log("🔄 Running Stripe backfill...");

      const { data, error } = await supabase.functions.invoke(
        "stripe-backfill",
        {
          body: {},
        },
      );

      if (error) throw error;

      setStatus((prev) => ({ ...prev, backfill: true }));
      toast({
        title: "Backfill Complete!",
        description: `Synced existing Stripe data: ${JSON.stringify(data.results)}`,
      });
    } catch (error: any) {
      console.error("Backfill error:", error);
      const errorData = error?.data || error;
      toast({
        variant: "destructive",
        title: "Backfill Failed",
        description:
          errorData?.hint ||
          errorData?.error ||
          error.message ||
          "Failed to sync existing Stripe data.",
      });
    } finally {
      setLoading((prev) => ({ ...prev, backfill: false }));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCw className="h-5 w-5" />
          Stripe Data Sync
        </CardTitle>
        <CardDescription>
          Set up automatic syncing of Stripe data to your Supabase database
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Step 1: Setup */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="flex-1">
            <h3 className="font-medium flex items-center gap-2">
              <Database className="h-4 w-4" />
              1. Create Sync Tables
              {status.setup && <Badge variant="secondary">Done</Badge>}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Creates stripe_customers, stripe_subscriptions, etc.
            </p>
          </div>
          <Button
            onClick={runSetup}
            disabled={loading.setup || status.setup}
            variant={status.setup ? "secondary" : "default"}
          >
            {loading.setup && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {status.setup ? "Setup Complete" : "Run Setup"}
          </Button>
        </div>

        {/* Step 2: Backfill */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="flex-1">
            <h3 className="font-medium flex items-center gap-2">
              <Upload className="h-4 w-4" />
              2. Sync Existing Data
              {status.backfill && <Badge variant="secondary">Done</Badge>}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Imports all your current Stripe customers, subscriptions, etc.
            </p>
          </div>
          <Button
            onClick={runBackfill}
            disabled={loading.backfill || !status.setup}
            variant={status.backfill ? "secondary" : "default"}
          >
            {loading.backfill && (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            )}
            {status.backfill ? "Backfill Complete" : "Run Backfill"}
          </Button>
        </div>

        {/* Step 3: Manual Instructions */}
        <div className="p-4 bg-blue-50 rounded-lg">
          <h3 className="font-medium text-blue-900 mb-2">
            3. Update Stripe Webhook (Manual)
          </h3>
          <p className="text-sm text-blue-700 mb-2">
            In your Stripe Dashboard → Webhooks, add this endpoint:
          </p>
          <code className="text-xs bg-blue-100 px-2 py-1 rounded">
            https://hyvouvjrxhmbxroagiin.supabase.co/functions/v1/stripe-sync
          </code>
          <p className="text-xs text-blue-600 mt-2">
            Use your existing webhook secret - it will work fine!
          </p>
        </div>

        {status.setup && status.backfill && (
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <h3 className="font-medium text-green-900 mb-1">
              🎉 Setup Complete!
            </h3>
            <p className="text-sm text-green-700">
              Your Stripe data is now syncing automatically. Check your database
              for new tables starting with "stripe_"
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
